import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, AlertCircle } from 'lucide-react';
import { api, parseApiError } from '../lib/api';
import { Card } from './Card';
import { MORPH } from '../lib/motion';

// ─────────────────────────────────────────────────────────────────────────────
// IncomingRoleRequestsPanel — shown to org owners; pending requests with
// approve/deny. Founder-only on ORG_OWNER targets (button disabled with
// tooltip for non-founders). Extracted from MyOrg so the same panel serves
// both the org page and the Setup Organisation step.
// ─────────────────────────────────────────────────────────────────────────────

// Same shortId idiom as OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/
// CompetitionMembersPanel — never render a raw Discord snowflake.
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

export function IncomingRoleRequestsPanel({ orgId, isFounder }: { orgId: string; isFounder: boolean }) {
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const [noteByReq, setNoteByReq] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['incomingRoleRequests', orgId],
    queryFn: () => api.listOrgRoleRequests(orgId, 'PENDING'),
    refetchInterval: 30_000,
  });

  const approveMut = useMutation({
    mutationFn: ({ requestId, note }: { requestId: string; note?: string }) =>
      api.approveRoleRequest(orgId, requestId, note),
    onSuccess: () => {
      setError('');
      qc.invalidateQueries({ queryKey: ['incomingRoleRequests', orgId] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const denyMut = useMutation({
    mutationFn: ({ requestId, note }: { requestId: string; note?: string }) =>
      api.denyRoleRequest(orgId, requestId, note),
    onSuccess: () => {
      setError('');
      qc.invalidateQueries({ queryKey: ['incomingRoleRequests', orgId] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  if (isLoading) return null;
  const requests = data?.requests ?? [];
  if (requests.length === 0) return null;

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-ink-secondary" />
        <h2 className="font-display text-xs font-black uppercase tracking-wider text-ink-meta">Incoming role requests</h2>
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-meta">
          {data?.pendingCount ?? 0} pending
        </span>
      </div>
      {error && (
        <p className="mb-2 flex items-start gap-2 text-xs text-red-400">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      )}
      <div className="space-y-2">
        {requests.map(r => {
          const ownerTarget = r.requestedRole === 'ORG_OWNER';
          const denyDisabled = ownerTarget && !isFounder;
          const note = noteByReq[r.requestId] ?? '';
          return (
            <div key={r.requestId} className="rounded-lg bg-white/[0.04] px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-body">
                  {r.requesterDisplayName ?? shortId(r.requesterDiscordId)}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-secondary">
                  {r.currentRole} → {r.requestedRole}
                </span>
                {r.scope === 'COMP' && r.scopeCompId && (
                  <span className="font-mono text-[10px] text-ink-meta">on {r.scopeCompId.replace(/^COMP#/, '')}</span>
                )}
                <span className="ml-auto font-mono text-[10px] text-ink-meta">
                  {new Date(r.createdAt).toLocaleDateString()}
                </span>
              </div>
              {r.reason && (
                <p className="mt-1 text-[11px] text-ink-secondary whitespace-pre-wrap">{r.reason}</p>
              )}
              <textarea
                value={note}
                onChange={e => setNoteByReq(s => ({ ...s, [r.requestId]: e.target.value.slice(0, 500) }))}
                placeholder="Optional resolver note (max 500 chars)…"
                className="input mt-2 w-full min-h-[40px] text-[11px]"
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={approveMut.isPending || denyMut.isPending || denyDisabled}
                  title={denyDisabled ? 'Only founders can promote to ORG_OWNER' : undefined}
                  onClick={() => approveMut.mutate({ requestId: r.requestId, note: note || undefined })}
                  className={`btn-ghost ${MORPH} inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-200 hover:text-emerald-100 disabled:opacity-50`}
                >
                  Approve
                </button>
                <button
                  type="button"
                  disabled={approveMut.isPending || denyMut.isPending || denyDisabled}
                  title={denyDisabled ? 'Only founders can resolve ORG_OWNER requests' : undefined}
                  onClick={() => denyMut.mutate({ requestId: r.requestId, note: note || undefined })}
                  className={`btn-ghost ${MORPH} inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-red-300 hover:text-red-200 disabled:opacity-50`}
                >
                  Deny
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
